import { useState, useEffect } from 'react'
import { useAppSelector, useAppDispatch } from '../../hooks/useAppDispatch'
import { setUser } from '../../store/slices/authSlice'
import { useUpdateProfileMutation, useUpdatePreferencesMutation, useGetUserQuery, useGetPreferencesQuery } from '../../store/api/usersApi'
import { useGetCategoriesQuery } from '../../store/api/categoriesApi'
import { useToast } from '../../context/ToastContext'
import { getErrorMessage } from '../../lib/apiClient'
import { SAVED_BANNER_TIMEOUT_MS, COLOR_ERROR, MAX_DISPLAY_NAME_LEN, MAX_BIO_LEN, MESSAGES } from '../../lib/config'
import Avatar from '../../components/ui/Avatar'
import { Button, Card, TextInput, Textarea, Label, Alert, SectionCard, FilterChip } from '../../components/ui'
import { MultiSelectDropdown } from '../../components/ui/MultiSelectDropdown'
import s from '../../styles/pages/SettingsPage.module.css'

const FLAIR_PRESETS = [
  '📊 CA / Tax Professional', '🏥 MBBS Doctor', '⚖️ Lawyer',
  '🎓 UPSC Aspirant', '💪 Fitness Coach', '🍳 Home Chef',
  '✈️ Travel Blogger', '🎵 Music Producer', '💻 Software Engineer',
  '📈 Stock Trader', '🌱 Student', '🔬 Researcher',
]

const FieldRow = ({
  label, hint, children, style,
}: { label: string; hint?: string; children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={style}>
    <Label style={{ display: 'block', marginBottom: '0.5rem' }}>
      {label}{' '}
      {hint && <span className={s.fieldHint}>{hint}</span>}
    </Label>
    {children}
  </div>
)

export function SettingsProfile() {
  const { user } = useAppSelector((s) => s.auth)
  const dispatch = useAppDispatch()
  const toast = useToast()
  const [updateProfile, { isLoading }] = useUpdateProfileMutation()
  const [updatePreferences] = useUpdatePreferencesMutation()

  const { data: userData } = useGetUserQuery(user?.id ?? '', { skip: !user?.id })
  const { data: prefsData } = useGetPreferencesQuery(undefined, { skip: !user })
  const { data: categoriesData } = useGetCategoriesQuery()

  const [displayName, setDisplayName] = useState(user?.displayName ?? '')
  const [bio, setBio] = useState('')
  const [flair, setFlair] = useState('')
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [selectedInterests, setSelectedInterests] = useState<string[]>([])
  const [interestsSaved, setInterestsSaved] = useState(false)
  const [userNotes, setUserNotes] = useState('')
  const [notesSaved, setNotesSaved] = useState(false)
  const MAX_NOTES_LEN = 500

  const avatarUrl = userData?.user?.profile?.avatarUrl ?? user?.avatarUrl
  const categories = categoriesData?.categories ?? []

  useEffect(() => {
    if (userData?.user?.profile) {
      setDisplayName(userData.user.profile.displayName)
      setBio(userData.user.profile.bio ?? '')
      setFlair(userData.user.profile.flair ?? '')
    }
  }, [userData])

  useEffect(() => {
    if (prefsData?.preferences?.interests) setSelectedInterests(prefsData.preferences.interests)
    const savedNotes = (prefsData?.preferences as any)?.userNotes
    if (typeof savedNotes === 'string') setUserNotes(savedNotes)
  }, [prefsData])

  const handleSaveProfile = async () => {
    setError('')
    if (!displayName.trim()) { setError('Display name is required'); return }
    if (displayName.trim().length < 2) { setError('Display name must be at least 2 characters'); return }
    try {
      const result = await updateProfile({ displayName: displayName.trim(), bio: bio.trim() || undefined, flair: flair.trim() || null }).unwrap()
      const freshAvatarUrl = result.user.profile?.avatarUrl ?? user?.avatarUrl ?? null
      if (user) dispatch(setUser({ ...user, displayName: displayName.trim(), avatarUrl: freshAvatarUrl }))
      setSaved(true)
      setTimeout(() => setSaved(false), SAVED_BANNER_TIMEOUT_MS)
    } catch (err) {
      setError(getErrorMessage(err, MESSAGES.SAVE_FAILED))
    }
  }

  const handleSaveInterests = async () => {
    try {
      await updatePreferences({ interests: selectedInterests }).unwrap()
      setInterestsSaved(true)
      setTimeout(() => setInterestsSaved(false), SAVED_BANNER_TIMEOUT_MS)
      toast.success('Interests saved!')
    } catch { toast.error('Failed to save interests.') }
  }

  const handleSaveNotes = async () => {
    try {
      await updatePreferences({ userNotes } as any).unwrap()
      setNotesSaved(true)
      setTimeout(() => setNotesSaved(false), SAVED_BANNER_TIMEOUT_MS)
      toast.success('AI memory saved!')
    } catch { toast.error('Failed to save AI memory.') }
  }

  if (!user) return null

  return (
    <>
      <Card>
        {error && <Alert variant="error"   style={{ marginBottom: '1rem' }}>{error}</Alert>}
        {saved && <Alert variant="success" style={{ marginBottom: '1rem' }}>Profile saved successfully</Alert>}

        <div className={s.avatarRow}>
          <Avatar name={displayName || user.displayName} avatarUrl={avatarUrl} size={64} />
          <div>
            <div className={s.avatarLabel}>Profile picture</div>
            <div className={s.avatarHint}>Synced automatically from your Google account</div>
          </div>
        </div>

        <FieldRow label="Display name *" style={{ marginBottom: '1.25rem' }}>
          <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={MAX_DISPLAY_NAME_LEN} />
        </FieldRow>

        <FieldRow label="Bio" hint="(optional)" style={{ marginBottom: '1.25rem' }}>
          <Textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell others about yourself..." maxLength={MAX_BIO_LEN} rows={3} />
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'right', marginTop: '0.25rem' }}>{bio.length}/{MAX_BIO_LEN}</div>
        </FieldRow>

        <FieldRow label="Expertise flair" hint="(optional)" style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
            {FLAIR_PRESETS.map((preset) => (
              <Button key={preset} type="button" variant="ghost" size="sm"
                onClick={() => setFlair(flair === preset ? '' : preset)}
                style={{ borderRadius: '20px', ...(flair === preset && { border: '1px solid var(--accent-primary)', background: 'rgba(124,58,237,0.12)', color: 'var(--accent-primary)' }) }}>
                {preset}
              </Button>
            ))}
          </div>
          <TextInput value={flair} onChange={(e) => setFlair(e.target.value.slice(0, 40))} placeholder="Or type a custom flair..." maxLength={40} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {flair ? <>Preview: <strong style={{ color: 'var(--accent-primary)' }}>{displayName || 'You'}</strong> <span style={{ fontSize: '0.72rem', background: 'rgba(124,58,237,0.1)', color: 'var(--accent-primary)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: '20px', padding: '0.1rem 0.5rem', fontWeight: 500 }}>{flair}</span></> : 'No flair set'}
            </span>
            <span style={{ fontSize: '0.75rem', color: flair.length >= 40 ? COLOR_ERROR : 'var(--text-muted)' }}>{flair.length}/40</span>
          </div>
        </FieldRow>

        <FieldRow label="Email" style={{ marginBottom: '1.25rem' }}>
          <TextInput value={user.email} disabled hint="Connected via Google — cannot be changed" />
        </FieldRow>

        <div className={s.saveRow}>
          <Button size="sm" onClick={() => void handleSaveProfile()} loading={isLoading}>Save changes</Button>
        </div>
      </Card>

      <SectionCard
        title="🎯 My Interests"
        description="Personalises your ✨ For You feed. You can update this any time."
        action={interestsSaved ? <span style={{ fontSize: '0.78rem', color: 'var(--status-success)', fontWeight: 600 }}>✓ Saved</span> : undefined}
      >
        {selectedInterests.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
            {selectedInterests.map(slug => {
              const cat = categories.find(c => c.slug === slug)
              if (!cat) return null
              return (
                <FilterChip
                  key={slug}
                  label={cat.name}
                  icon={cat.icon ?? undefined}
                  color={cat.color ?? undefined}
                  onRemove={() => setSelectedInterests(prev => prev.filter(s => s !== slug))}
                />
              )
            })}
          </div>
        )}
        <MultiSelectDropdown
          options={categories.map(cat => ({ value: cat.slug, label: cat.name, icon: cat.icon ?? undefined, color: cat.color ?? undefined }))}
          selected={selectedInterests}
          onChange={setSelectedInterests}
          placeholder="Select topics…"
          searchPlaceholder="Search topics…"
          fullWidth
          maxHeight={220}
          align="left"
        />
        <div className={s.interestsFooter}>
          <span className={s.interestsHint}>{selectedInterests.length === 0 ? 'No interests selected — all topics will appear in your feed' : `${selectedInterests.length} topic${selectedInterests.length > 1 ? 's' : ''} selected`}</span>
          <Button size="sm" onClick={() => void handleSaveInterests()}>Save interests</Button>
        </div>
      </SectionCard>

      <SectionCard
        title="🧠 AI Memory"
        description="Tell the AI about yourself — diet, location, goals. It remembers this across every response."
        action={notesSaved ? <span style={{ fontSize: '0.78rem', color: 'var(--status-success)', fontWeight: 600 }}>✓ Saved</span> : undefined}
      >
        <div className={s.notesHint}>Examples: "I'm vegetarian", "I live in Hyderabad", "I'm preparing for UPSC 2026"</div>
        <Textarea value={userNotes} onChange={(e) => setUserNotes(e.target.value.slice(0, MAX_NOTES_LEN))} placeholder="Tell the AI about yourself..." rows={4} />
        <div className={s.notesFooter}>
          <span style={{ fontSize: '0.72rem', color: userNotes.length >= MAX_NOTES_LEN ? COLOR_ERROR : 'var(--text-muted)' }}>{userNotes.length}/{MAX_NOTES_LEN}</span>
          <Button size="sm" onClick={() => void handleSaveNotes()}>Save memory</Button>
        </div>
      </SectionCard>
    </>
  )
}
